import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../main/initializers/axios';
import { ApiConstants } from '../../main/constants/ApiConstants';
import './MyUnpaidInvoices.css';
import BasicCli from '../../main/components/basicCli/BasicCli';

type UnpaidInvoicesPageVm = {
  unpaidTotal: number;
  tables: TableFilterVm[];
  invoices: UnpaidInvoiceCardVm[];
};

type TableFilterVm = {
  tableCode: string;
};

type UnpaidInvoiceCardVm = {
  invoiceId: number;
  tableCode: string;
  created?: string;
  Created?: string;
  total: number;
  rows: UnpaidInvoiceRowVm[];
};

type UnpaidInvoiceRowVm = {
  productName: string;
  quantity: number;
  lineTotal: number;
};

const money = (n: number) =>
  new Intl.NumberFormat('sq-AL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n) || 0);

const qty = (n: number) =>
  new Intl.NumberFormat('sq-AL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  }).format(Number(n) || 0);

const unitPrice = (row: UnpaidInvoiceRowVm) => {
  const quantity = Number(row.quantity) || 0;
  if (quantity <= 0) return Number(row.lineTotal) || 0;
  return (Number(row.lineTotal) || 0) / quantity;
};

const formatInvoiceDateTime = (value?: string) => {
  if (!value) return '-';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';

  return new Intl.DateTimeFormat('sq-AL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
    .format(date)
    .replace(/\//g, '.')
    .replace(',', '');
};

const MyUnpaidInvoices: React.FC = () => {
  const { t } = useTranslation();

  const [data, setData] = useState<UnpaidInvoicesPageVm | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [tableCode, setTableCode] = useState<string>('');
  const [filterInput, setFilterInput] = useState<string>('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const tRef = useRef(t);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();
  const isInitialSearchEffectRef = useRef(true);
  const skipNextSearchEffectRef = useRef(false);
  const filterRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    tRef.current = t;
  }, [t]);

  const load = useCallback(
    async (code?: string) => {
      setErr(null);
      setLoading(true);

      try {
        const params: any = {};
        if (code) params.tableCode = code;

        const res = await axiosInstance.get(ApiConstants.invoice.myUnpaid, { params });

        const payload: UnpaidInvoicesPageVm = {
          unpaidTotal: Number(res?.data?.unpaidTotal) || 0,
          tables: res?.data?.tables ?? [],
          invoices: res?.data?.invoices ?? [],
        };

        setData(payload);
      } catch (e: any) {
        setErr(e?.response?.data?.message ?? tRef.current('invoice.myOpenedInvoicesLoadError', { defaultValue: 'Error while loading invoices.' }));
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  // Click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  useEffect(() => {
    if (isInitialSearchEffectRef.current) {
      isInitialSearchEffectRef.current = false;
      return;
    }

    if (skipNextSearchEffectRef.current) {
      skipNextSearchEffectRef.current = false;
      return;
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setTableCode(filterInput);
      load(filterInput);
    }, 500);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [filterInput, load]);

  // Initial load
  useEffect(() => {
    load();
  }, [load]);

  const invoices = useMemo(() => data?.invoices ?? [], [data]);
  const tables = useMemo(() => data?.tables ?? [], [data]);
  const selectedInvoice = useMemo(
    () => invoices.find((invoice) => invoice.invoiceId === selectedInvoiceId) ?? invoices[0] ?? null,
    [invoices, selectedInvoiceId],
  );

  const groupedInvoices = useMemo(() => {
    return invoices.reduce<Array<{ tableCode: string; total: number; invoices: UnpaidInvoiceCardVm[] }>>((groups, invoice) => {
      const groupCode = invoice.tableCode || `#${invoice.invoiceId}`;
      const existingGroup = groups.find((group) => group.tableCode === groupCode);

      if (existingGroup) {
        existingGroup.total += Number(invoice.total) || 0;
        existingGroup.invoices.push(invoice);
        return groups;
      }

      groups.push({
        tableCode: groupCode,
        total: Number(invoice.total) || 0,
        invoices: [invoice],
      });

      return groups;
    }, []);
  }, [invoices]);

  const unpaidTotal = useMemo(() => {
    if (data?.unpaidTotal) return data.unpaidTotal;
    return invoices.reduce((sum, x) => sum + (Number(x.total) || 0), 0);
  }, [data, invoices]);

  const filteredTables = useMemo(() => {
    if (!filterInput) return tables;
    return tables.filter((table) => table.tableCode.toLowerCase().includes(filterInput.toLowerCase()));
  }, [tables, filterInput]);

  useEffect(() => {
    if (invoices.length === 0) {
      setSelectedInvoiceId(null);
      setIsPreviewOpen(false);
      return;
    }

    if (!selectedInvoiceId || !invoices.some((invoice) => invoice.invoiceId === selectedInvoiceId)) {
      setSelectedInvoiceId(invoices[0].invoiceId);
    }
  }, [invoices, selectedInvoiceId]);

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFilterInput(e.target.value);
    setShowSuggestions(true);
  };

  const handleSuggestionClick = (code: string) => {
    if (code !== filterInput) {
      skipNextSearchEffectRef.current = true;
    }
    setFilterInput(code);
    setTableCode(code);
    setShowSuggestions(false);
    load(code);
  };

  const handleClearFilter = () => {
    if (filterInput !== '') {
      skipNextSearchEffectRef.current = true;
    }
    setFilterInput('');
    setTableCode('');
    setShowSuggestions(false);
    load('');
  };

  const handleRefresh = () => {
    if (tableCode !== filterInput) {
      skipNextSearchEffectRef.current = true;
    }
    setFilterInput(tableCode);
    load(tableCode);
  };

  const handleFocus = () => {
    if (filterInput && filteredTables.length > 0) {
      setShowSuggestions(true);
    }
  };

  return (
    <BasicCli>
      <div className="muiRoot">
        {/* Header me filter */}
        <div className="muiHeader">
          <div className="muiHeaderLeft">
            <h2>{t('invoice.myOpenedInvoicesTitle', { defaultValue: 'Opened invoices' })}</h2>
            <div className="muiSub">{t('invoice.myOpenedInvoicesSubtitle', { defaultValue: 'Unpaid invoices for the logged in user' })}</div>
          </div>

          {/* Filter në header */}
          <div className="muiHeaderFilter" ref={filterRef}>
            <div className="muiFilterContainer">
              <input
                type="text"
                value={filterInput}
                onChange={handleFilterChange}
                onFocus={handleFocus}
                placeholder={t('invoice.searchTable', { defaultValue: 'Search by table code...' })}
                className="muiHeaderSearchInput"
                disabled={loading}
              />
              {filterInput && (
                <button className="muiHeaderClearFilter" onClick={handleClearFilter} type="button">
                  ×
                </button>
              )}

              {/* Table suggestions dropdown */}
              {showSuggestions && filterInput && filteredTables.length > 0 && (
                <div className="muiHeaderSuggestions">
                  {filteredTables.slice(0, 5).map((table) => (
                    <button key={table.tableCode} className="muiSuggestionItem" onClick={() => handleSuggestionClick(table.tableCode)} type="button">
                      {table.tableCode}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="muiHeaderRight">
            <div className="muiMetric">
              <div className="muiTurnoverLabel">{t('invoice.myOpenedInvoicesReceivable', { defaultValue: 'To receive' })}</div>
              <div className="muiTurnoverValue">{money(unpaidTotal)} ALL</div>
            </div>

            <button className="muiBtn" onClick={handleRefresh} type="button" disabled={loading}>
              {loading ? (
                <>
                  <span className="muiSpinner" />
                  {t('invoice.myOpenedInvoicesRefreshing', { defaultValue: 'Refreshing...' })}
                </>
              ) : (
                t('invoice.myOpenedInvoicesRefresh', { defaultValue: 'Refresh' })
              )}
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loading && !err && (
          <div className="muiLoading">
            <div className="muiSpinner" />
            <span>{t('common.loading', { defaultValue: 'Loading...' })}</span>
          </div>
        )}

        {/* Error State */}
        {err && (
          <div className="muiError" role="alert">
            <span className="muiErrorIcon">⚠️</span>
            {err}
            <button className="muiRetryBtn" onClick={() => load(tableCode)} type="button">
              {t('common.retry', { defaultValue: 'Retry' })}
            </button>
          </div>
        )}

        {/* Empty State */}
        {!err && !loading && invoices.length === 0 && (
          <div className="muiEmpty">
            {filterInput
              ? t('invoice.noInvoicesForTable', {
                  defaultValue: 'No opened invoices found for table "{{tableCode}}"',
                  tableCode: filterInput,
                })
              : t('invoice.myOpenedInvoicesEmpty', { defaultValue: 'There are no opened invoices at the moment.' })}
          </div>
        )}

        {/* Invoice groups and preview */}
        {!loading && invoices.length > 0 && (
          <div className="muiInvoiceWorkspace">
            <div className="muiGroupsPane">
              <div className="muiPaneHeader">
                <div>
                  <div className="muiPaneTitle">{t('invoice.invoiceGroups', { defaultValue: 'Invoice groups' })}</div>
                  <div className="muiPaneHint">
                    {t('invoice.clickInvoiceToPreview', { defaultValue: 'Click an invoice to open it on the right' })}
                  </div>
                </div>
                <div className="muiGroupCount">{groupedInvoices.length}</div>
              </div>

              <div className="muiGroupList">
                {groupedInvoices.map((group) => (
                  <section key={group.tableCode} className="muiInvoiceGroup">
                    <div className="muiGroupHeader">
                      <div>
                        <div className="muiGroupTable">
                          {t('invoice.table', { defaultValue: 'Table' })}: <b>{group.tableCode}</b>
                        </div>
                        <div className="muiGroupMeta">
                          {t('invoice.totalInvoices', {
                            defaultValue: 'Total invoices: {{count}}',
                            count: group.invoices.length,
                          })}
                        </div>
                      </div>
                      <div className="muiGroupTotal">{money(group.total)} ALL</div>
                    </div>

                    <div className="muiGroupInvoices">
                      {group.invoices.map((inv) => {
                        const isSelected = selectedInvoice?.invoiceId === inv.invoiceId;

                        return (
                          <button
                            key={inv.invoiceId}
                            className={`muiInvoiceListItem${isSelected ? ' isSelected' : ''}`}
                            onClick={() => {
                              setSelectedInvoiceId(inv.invoiceId);
                              setIsPreviewOpen(true);
                            }}
                            type="button"
                          >
                            <span className="muiInvoiceTileTop">
                              <span className="muiInvoiceTileTable">{inv.tableCode || group.tableCode}</span>
                              <span className="muiInvoiceTileId">#{inv.invoiceId}</span>
                            </span>
                            <span className="muiInvoiceTileDate">{formatInvoiceDateTime(inv.created ?? inv.Created)}</span>
                            <strong>{money(inv.total)} ALL</strong>
                          </button>
                        );
                      })}
                    </div>
                  </section>
                ))}
              </div>
            </div>

            <aside className={`muiPreviewPane${isPreviewOpen ? ' isMobileOpen' : ''}`} aria-live="polite" onClick={() => setIsPreviewOpen(false)}>
              {selectedInvoice && (
                <div className="muiInvoicePreview" onClick={(event) => event.stopPropagation()}>
                  <button
                    className="muiPreviewClose"
                    type="button"
                    aria-label={t('common.close', { defaultValue: 'Close' })}
                    onClick={() => setIsPreviewOpen(false)}
                  >
                    ×
                  </button>
                  <div className="muiPreviewTop">
                    <div className="muiPreviewLabel">{t('invoice.invoicePreview', { defaultValue: 'Invoice' })}</div>
                    <h3>{t('invoice.invoiceNumber', { defaultValue: 'Invoice #{{id}}', id: selectedInvoice.invoiceId })}</h3>
                  </div>

                  <div className="muiReceiptMeta muiReceiptMetaInline">
                    <div className="muiReceiptMetaItem">
                      <span>{t('invoice.table', { defaultValue: 'Table' })}:</span>
                      <strong>{selectedInvoice.tableCode || `#${selectedInvoice.invoiceId}`}</strong>
                    </div>
                    <div className="muiReceiptMetaItem">
                      <span>{t('invoice.dateTime', { defaultValue: 'Date/time' })}:</span>
                      <strong>{formatInvoiceDateTime(selectedInvoice.created ?? selectedInvoice.Created)}</strong>
                    </div>
                  </div>

                  <div className="muiReceiptTable">
                    <div className="muiReceiptHead">
                      <span>{t('invoice.product', { defaultValue: 'Product' })}</span>
                      <span>{t('invoice.quantityShort', { defaultValue: 'Qty' })}</span>
                      <span>{t('invoice.price', { defaultValue: 'Price' })}</span>
                      <span>{t('invoice.value', { defaultValue: 'Value' })}</span>
                    </div>

                    {(selectedInvoice.rows ?? []).map((row, idx) => (
                      <div key={`${selectedInvoice.invoiceId}-preview-${idx}`} className="muiPreviewRow">
                        <strong>{row.productName}</strong>
                        <span>{qty(row.quantity)}</span>
                        <span>{money(unitPrice(row))}</span>
                        <b>{money(row.lineTotal)}</b>
                      </div>
                    ))}
                  </div>

                  <div className="muiPreviewTotal">
                    <span>{t('invoice.totalLabel', { defaultValue: 'Total' })}</span>
                    <strong>{money(selectedInvoice.total)} ALL</strong>
                  </div>

                  <div className="muiReceiptFooter">
                    <div className="muiFakeQr" aria-hidden="true" />
                    <div className="muiReceiptThanks">{t('invoice.thankYouMessage', { defaultValue: 'Thank you!' })}</div>
                  </div>
                </div>
              )}
            </aside>
          </div>
        )}

      </div>
    </BasicCli>
  );
};

export default MyUnpaidInvoices;
